import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, IconButton, InputAdornment, OutlinedInput, Paper, Popper } from '@mui/material';
import { Search as SearchIcon, X, ArrowUpRight, Package, Boxes, Layers3, ShoppingBag, SearchX, CircleAlert } from 'lucide-react';
import { searchApi } from '../../../../api/search/search.api';
import './search.css';

const types = {
  order: { label: 'Orders', singular: 'Order', Icon: ShoppingBag, tone: 'blue' },
  product_catalog: { label: 'Catalog', singular: 'Catalog', Icon: Package, tone: 'amber' },
  listing: { label: 'Listings', singular: 'Listing', Icon: Layers3, tone: 'teal' },
  fulfilment_product: { label: 'Inventory', singular: 'Inventory', Icon: Boxes, tone: 'violet' }
};
function resultPath(result) {
  const id = result.type === 'order' ? result.salesChannelOrderId : result.id;
  if (id == null) return null;
  const base = {
    order: '/orders/order-details/',
    product_catalog: '/product-catalog/',
    listing: '/listing/',
    fulfilment_product: '/inventory/'
  }[result.type];
  return base ? `${base}${encodeURIComponent(id)}` : null;
}
function resultDetails(result) {
  const variant =
    result.type === 'product_catalog' ? result.ProductCatalogVariant?.[0] : result.type === 'listing' ? result.ListingVariant?.[0] : result;
  const fields =
    result.type === 'order'
      ? [
          ['Customer', result.customerName],
          ['Total', result.totalPrice != null ? `${result.currency || '$'} ${result.totalPrice}` : null],
          ['Status', result.financialStatus]
        ]
      : [
          ['SKU', variant?.sku],
          ['Brand', result.brand],
          ['UPC', variant?.upc],
          ['MPN', variant?.mpn],
          ['Wholesale', result.wholesalePrice != null ? `$${result.wholesalePrice}` : null],
          ['MSRP', variant?.msrp != null ? `$${variant.msrp}` : null],
          ['Price', result.type === 'listing' && variant?.listPrice != null ? `${result.currency || '$'} ${variant.listPrice}` : null],
          ['Status', result.type === 'listing' ? result.status : null]
        ];
  return fields.filter(([, value]) => value != null && value !== '');
}
function highlight(value, query) {
  const text = String(value);
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0 || !query) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}
export default function Search() {
  const navigate = useNavigate();
  const id = useId();
  const root = useRef(null);
  const popup = useRef(null);
  const input = useRef(null);
  const request = useRef(0);
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(-1);
  const filtered = useMemo(() => (filter === 'all' ? results : results.filter((result) => result.type === filter)), [results, filter]);
  const close = () => {
    request.current += 1;
    setOpen(false);
    setLoading(false);
    setActive(-1);
  };
  const clear = () => {
    close();
    setQuery('');
    setResults([]);
    setSearched('');
    setError(false);
    setFilter('all');
    input.current?.focus();
  };
  const search = async () => {
    const value = query.trim();
    if (!value) return;
    const sequence = ++request.current;
    setLoading(true);
    setOpen(true);
    setError(false);
    setResults([]);
    setActive(-1);
    setFilter('all');
    setSearched(value);
    try {
      const response = await searchApi.universalSearch(value);
      if (sequence !== request.current) return;
      setResults((Array.isArray(response.data.results) ? response.data.results : []).filter((result) => resultPath(result)));
    } catch {
      if (sequence === request.current) setError(true);
    } finally {
      if (sequence === request.current) setLoading(false);
    }
  };
  const choose = (result) => {
    const path = resultPath(result);
    if (path) {
      close();
      setQuery('');
      setResults([]);
      setSearched('');
      navigate(path);
    }
  };
  useEffect(() => {
    const outside = (event) => {
      if (!root.current?.contains(event.target) && !popup.current?.contains(event.target)) {
        request.current += 1;
        setOpen(false);
        setLoading(false);
        setActive(-1);
      }
    };
    const escape = (event) => {
      if (event.key === 'Escape') {
        request.current += 1;
        setOpen(false);
        setLoading(false);
        setActive(-1);
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      request.current += 1;
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, []);
  useEffect(() => {
    if (active >= 0) popup.current?.querySelector(`[data-result-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && active >= 0 && filtered[active]) choose(filtered[active]);
      else search();
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && open && filtered.length) {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) =>
        current < 0 ? (direction === 1 ? 0 : filtered.length - 1) : (current + direction + filtered.length) % filtered.length
      );
    }
    if (event.key === 'Tab') close();
  };
  return (
    <Box ref={root} className="xc-search-root" sx={{ width: '100%', position: 'relative' }}>
      <OutlinedInput
        fullWidth
        size="small"
        inputRef={input}
        value={query}
        onChange={(event) => {
          request.current += 1;
          setQuery(event.target.value);
          setOpen(false);
          setLoading(false);
          setResults([]);
          setActive(-1);
        }}
        onFocus={() => {
          if (searched === query.trim() && results.length) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder="Search products, SKU, orders…"
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon size={17} color="#8193aa" />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            {query && (
              <IconButton size="small" aria-label="Clear search" onClick={clear}>
                <X size={15} />
              </IconButton>
            )}
            <IconButton size="small" aria-label="Search" onClick={search} disabled={loading || !query.trim()}>
              {loading ? <CircularProgress size={16} /> : <ArrowUpRight size={16} />}
            </IconButton>
          </InputAdornment>
        }
        slotProps={{
          input: {
            role: 'combobox',
            'aria-label': 'Search products, inventory, listings, and orders',
            'aria-autocomplete': 'list',
            'aria-expanded': open,
            'aria-controls': open ? `${id}-results` : undefined,
            'aria-activedescendant': open && active >= 0 ? `${id}-option-${active}` : undefined
          }
        }}
      />
      <Popper
        open={open}
        anchorEl={root.current}
        placement="bottom-end"
        sx={{ zIndex: 1400 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 10] } },
          { name: 'preventOverflow', options: { padding: 16 } },
          { name: 'flip', options: { padding: 16 } }
        ]}
      >
        <Paper ref={popup} className="xc-search-popup" elevation={0}>
          <div className="xc-search-heading">
            <div>
              <strong>Search results</strong>
              <span role="status" aria-live="polite">
                {loading
                  ? 'Searching…'
                  : error
                    ? 'Search unavailable'
                    : `${results.length} ${results.length === 1 ? 'match' : 'matches'} for “${searched}”`}
              </span>
            </div>
            <IconButton
              size="small"
              aria-label="Close search results"
              onClick={() => {
                close();
                input.current?.focus();
              }}
            >
              <X size={17} />
            </IconButton>
          </div>
          {!loading && !error && results.length > 0 && (
            <div className="xc-search-filters" aria-label="Filter search results">
              {[
                ['all', 'All'],
                ...Object.entries(types)
                  .filter(([key]) => results.some((result) => result.type === key))
                  .map(([key, type]) => [key, type.label])
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={filter === key}
                  className={filter === key ? 'selected' : ''}
                  onClick={() => {
                    setFilter(key);
                    setActive(-1);
                    input.current?.focus();
                  }}
                >
                  {label}
                  <span>{key === 'all' ? results.length : results.filter((result) => result.type === key).length}</span>
                </button>
              ))}
            </div>
          )}
          <div className="xc-search-scroll" id={`${id}-results`} role="listbox" aria-label="Search results" aria-busy={loading}>
            {loading ? (
              <div className="xc-search-empty">
                <CircularProgress size={24} />
                <strong>Finding matches…</strong>
              </div>
            ) : error ? (
              <div className="xc-search-empty">
                <CircleAlert size={26} />
                <strong>Search couldn’t be completed</strong>
                <p>Please try again.</p>
                <button type="button" onClick={search}>
                  Retry search
                </button>
              </div>
            ) : filtered.length ? (
              filtered.map((result, index) => {
                const type = types[result.type];
                const Icon = type.Icon;
                return (
                  <div
                    key={`${result.type}-${result.id}-${index}`}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={active === index}
                    tabIndex={-1}
                    data-result-index={index}
                    className={`xc-search-result ${active === index ? 'is-active' : ''}`}
                    onPointerMove={() => setActive(index)}
                    onClick={() => choose(result)}
                  >
                    <span className={`xc-search-result-icon ${type.tone}`}>
                      <Icon size={19} />
                    </span>
                    <div className="xc-search-result-body">
                      <div className="xc-search-result-top">
                        <strong>{highlight(result.name || result.orderNumber || result.id, searched)}</strong>
                        <span className={`xc-search-type ${type.tone}`}>{type.singular}</span>
                      </div>
                      <dl>
                        {resultDetails(result).map(([label, value]) => (
                          <div key={label}>
                            <dt>{label}</dt>
                            <dd>{highlight(value, searched)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                    <ArrowUpRight className="xc-search-result-arrow" size={15} />
                  </div>
                );
              })
            ) : (
              <div className="xc-search-empty">
                <SearchX size={28} />
                <strong>No matches found</strong>
                <p>Try a product name, SKU, UPC, or order number.</p>
              </div>
            )}
          </div>
          <div className="xc-search-footer">
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> Navigate <kbd>↵</kbd> Open
            </span>
            <span>
              <kbd>Esc</kbd> Close
            </span>
          </div>
        </Paper>
      </Popper>
    </Box>
  );
}
